// Kept identical in the public and admin SPAs. No storage: each mounted quiz is a new attempt.
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
export interface Snapshot {
  prospectEmail: string | null;
  responses: Record<number, unknown>;
}
export interface Attempt {
  attemptId: string;
  prospectEmail: string;
  token?: string;
  expiresAt?: string;
}
export interface AutosaveTransport {
  session: (email: string | null) => Promise<{ token: string; expiresAt: string }>;
  draft: (snapshot: Snapshot, attempt: Attempt) => Promise<unknown>;
}

function hasInput(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  if (typeof value === 'object') return Object.values(value).some(hasInput);
  return true;
}

export function normalizeEmail(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const email = value.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

// A nonempty marked field takes precedence, including when invalid (fail closed).
export function effectiveProspectEmail(
  quiz: { questions: Array<{ id: number; type: string; formFields?: Array<{ id: number; type: string; isProspectEmail?: boolean }> }> } | null,
  responses: Record<number, unknown>,
  inherited: unknown,
): string | null {
  for (const question of quiz?.questions ?? []) {
    if (question.type !== 'FormEntry' && question.type !== 'CustomerInsightsForm') continue;
    for (const field of question.formFields ?? []) {
      if (field.type !== 'email' || !field.isProspectEmail) continue;
      const values = responses[question.id];
      const value = values && typeof values === 'object'
        ? (values as Record<string, unknown>)[`field_${field.id}`] : undefined;
      if (typeof value === 'string' && value.trim()) return normalizeEmail(value);
    }
  }
  return normalizeEmail(inherited);
}

export class AssessmentAutosave {
  status: SaveStatus = 'idle';
  emailChanged = false;
  private active = false;
  private finished = false;
  private timer?: ReturnType<typeof setTimeout>;
  private pending?: Snapshot;
  private running?: Promise<void>;
  private attempt?: Attempt;
  private anonymousSession?: { token: string; expiresAt: string };
  private lastSaved?: string;
  private latest: Snapshot = { prospectEmail: null, responses: {} };
  private listener?: () => void;

  constructor(private transport: AutosaveTransport, private delay = 500) {}

  get isActive(): boolean { return this.active; }

  invalidateSessionToken(token?: string): void {
    if (this.attempt && (!token || this.attempt.token === token)) this.attempt.token = undefined;
    if (this.anonymousSession && (!token || this.anonymousSession.token === token)) this.anonymousSession = undefined;
  }

  start(listener: () => void): void {
    this.active = true;
    this.listener = listener;
    if (this.pending) this.schedule();
  }

  // Reversible cleanup supports React StrictMode effect replay without changing the UUID.
  stop(): void {
    this.active = false;
    this.listener = undefined;
    this.clearTimer();
  }

  private notify(status: SaveStatus): void {
    this.status = status;
    if (this.active) this.listener?.();
  }

  private clearTimer(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  update(snapshot: Snapshot): void {
    if (this.finished) return;
    this.latest = JSON.parse(JSON.stringify(snapshot)) as Snapshot;
    this.clearTimer();
    const email = snapshot.prospectEmail;
    if (!email) {
      this.pending = undefined;
      this.notify('idle');
      return;
    }
    this.anonymousSession = undefined;
    if (!this.attempt && !hasInput(snapshot.responses)) return;
    if (!this.attempt || this.attempt.prospectEmail !== email) {
      this.emailChanged ||= Boolean(this.attempt);
      this.attempt = { attemptId: crypto.randomUUID(), prospectEmail: email };
      this.lastSaved = undefined;
    }
    // Knowing an email alone does not create a blank draft.
    if (!this.running && JSON.stringify(this.latest) === this.lastSaved) {
      this.pending = undefined;
      this.notify(this.lastSaved ? 'saved' : 'idle');
      return;
    }
    this.pending = this.latest;
    this.notify('saving');
    this.schedule();
  }

  private schedule(): void {
    this.clearTimer();
    if (!this.active || this.finished) return;
    this.timer = setTimeout(() => { void this.flush().catch(() => { /* Inline error + explicit/input retry. */ }); }, this.delay);
  }

  private async session(attempt: Attempt): Promise<string> {
    if (!attempt.token || (attempt.expiresAt && Date.parse(attempt.expiresAt) <= Date.now())) {
      const session = await this.transport.session(attempt.prospectEmail);
      attempt.token = session.token;
      attempt.expiresAt = session.expiresAt;
    }
    return attempt.token;
  }

  async flush(): Promise<void> {
    this.clearTimer();
    if (this.running) {
      await this.running;
      if (this.pending && this.active) return this.flush();
      return;
    }
    const work = async (): Promise<void> => {
      while (this.pending && this.active && !this.finished) {
        const snapshot = this.pending;
        const attempt = this.attempt!;
        this.pending = undefined;
        this.notify('saving');
        try {
          await this.session(attempt);
          // Email edits/unmount during session creation must not send the old snapshot.
          if (!this.active || attempt !== this.attempt || !this.latest.prospectEmail) continue;
          await this.transport.draft(snapshot, attempt);
          if (attempt === this.attempt && this.latest.prospectEmail) {
            this.lastSaved = JSON.stringify(snapshot);
            this.notify(this.pending ? 'saving' : 'saved');
          }
        } catch (error) {
          if (attempt !== this.attempt || !this.latest.prospectEmail) continue;
          // Renew an expired/rejected token only on the next explicit retry/input.
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 401 || status === 403) attempt.token = undefined;
          this.pending ??= snapshot;
          this.clearTimer();
          this.notify('error');
          throw error;
        }
      }
    };
    this.running = work();
    try { await this.running; } finally { this.running = undefined; }
  }

  async prepareCompletion(options: { allowAnonymous?: boolean; reuseAnonymousSession?: boolean } = {}): Promise<{ token: string; attemptId?: string; prospectEmail: string | null }> {
    if (!this.latest.prospectEmail) {
      if (options.allowAnonymous === false || (this.attempt && !options.allowAnonymous)) {
        throw new Error('Enter a valid prospect email before completing this saved attempt.');
      }
      if (this.attempt) {
        this.attempt = undefined;
        this.pending = undefined;
        this.lastSaved = undefined;
      }
    }
    await this.flush();
    if (!this.active) throw new Error('Quiz is no longer active.');
    if (this.attempt) {
      return { token: await this.session(this.attempt), attemptId: this.attempt.attemptId, prospectEmail: this.attempt.prospectEmail };
    }
    if (!this.latest.prospectEmail && options.reuseAnonymousSession) {
      if (!this.anonymousSession || Date.parse(this.anonymousSession.expiresAt) <= Date.now()) {
        this.anonymousSession = await this.transport.session(null);
      }
      return { token: this.anonymousSession.token, prospectEmail: null };
    }
    const session = await this.transport.session(this.latest.prospectEmail);
    return { token: session.token, prospectEmail: this.latest.prospectEmail };
  }

  complete(): void {
    this.finished = true;
    this.pending = undefined;
    this.clearTimer();
  }
}